import AppliedFlowDescription from '../flows/AppliedFlowDescription.react';
import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import urlMapping from '../app/URLMapping';
import types from '../../common/model-api/types';
import * as Tool from '../fw/Tool';
import {Button, Alert} from 'react-bootstrap';

import {scenarioRequiresPayPlan} from '../../common/model-api/ScenarioType';
import {FormattedMessage, injectIntl, IntlShape} from 'react-intl';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import ApplyScenarioMutation from './ApplyScenarioMutation.react';
import ScenarioBoxAsWizard from '../scenarios/ScenarioBoxAsWizard.react';
import ScenarioIcon from '../scenarios/ScenarioIcon.react';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import ChannelIcon from "../channel-defs/ChannelIcon.react";
import config from '../../common/config';
import {getExtensionURL, sendToExtension, isExtensionPresent} from '../app/ExtensionCommunicate';
import {LoadingLine} from '../loadingLine/renderLoadingPanel.react';
import EventTracker from '../app/EventTracker';
import mutationError from '../channel-defs/mutationError';
import StepperList from '../elements/StepperList.react';
import DetailBox from '../fw/DetailBox.react';
import meQuery from '../queries/MeQuery';
import FlowConfig from '../flows/FlowConfig.react';
import ChannelChooser from './ScenarioChooser.react';
import ScenarioChannelConnect from './ScenarioChannelConnect.react';
import ScenarioPrerequisite from './ScenarioPrerequisite.react';
import Link from 'react-router/lib/Link';

import './Scenario.scss';
import { User } from '../../common/types/types';

interface ScenarioApplyProps {
 intl: IntlShape;
 location?: {
  query?: {[key: string]: any};
  search?: string;
 };
 relay: object;
 user: User;
}

export class ScenarioApply extends React.Component<ScenarioApplyProps> {

    typeName = 'scenarios';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired
    };

    static contextTypes = {
        router: PropTypes.object
    };

    constructor(props) {
        super(props);

        let scenario = this.getScenario();
        let channels = this.props.user.organisation.channels;
        const preconfiguredFlowId = ScenarioApply.getFlowIdFromQueryParams(props);
        const callbackOnSave = ScenarioApply.getCallbackOnSaveFromQueryParams(props);
        const closeWindow = ScenarioApply.getCloseWindowFromQueryParams(props);
        const showScenarioInfoStep = ScenarioApply.getShowScenarioInfoStepFromQueryParams(props);
        const grantedChannelId = ScenarioApply.getGrantedChannelIdFromQueryParams(props);
        const grantedStep = ScenarioApply.getGrantedStepFromQueryParams(props);
        const grantedChannelDefId = ScenarioApply.getRawQueryParams(props).grantedChannelDefId || null;

        const presetSelection = ScenarioApply.getPresetSelectionData(props, scenario, channels);

        if (typeof window !== 'undefined') {
            // Debug URL-driven scenario apply behavior in local environment.
            console.error('[ScenarioApply] init', {
                rawParams: ScenarioApply.getRawQueryParams(props),
                preconfiguredFlowId,
                closeWindow,
                showScenarioInfoStep,
                grantedChannelId,
                grantedStep,
                skippedGroups: Object.keys(presetSelection.skippedGroups || {}),
                lockedGroups: Object.keys(presetSelection.lockedGroups || {}),
                selectedGroups: Object.keys(presetSelection.channelSelect || {}),
            });
        }

        let {alreadyConnectedChannels, channelsToConnect} = this.getChannelsToConnect(scenario, channels);
        alreadyConnectedChannels = ScenarioApply.filterBySkippedGroup(alreadyConnectedChannels, presetSelection.skippedGroups);
        channelsToConnect = ScenarioApply.filterBySkippedGroup(channelsToConnect, presetSelection.skippedGroups);
        const channelsToSelect = ScenarioApply.filterBySkippedGroup(ScenarioApply.getChannelsToSelect(scenario, channels), presetSelection.skippedGroups);

        if (typeof window !== 'undefined' && grantedChannelId) {
            console.error('[ScenarioApply] channel lists', {
                grantedChannelId,
                alreadyConnectedChannels: alreadyConnectedChannels.map((i) => ({
                    groupKey: i.groupKey,
                    realId: i.channel ? i.channel.realId : null,
                    id: i.channel ? i.channel.id : null,
                })),
                channelsToConnect: channelsToConnect.map((i) => ({
                    groupKey: i.groupKey,
                })),
                channelsToSelect: channelsToSelect.map((i) => ({
                    groupKey: i.groupKey,
                    channelIds: i.channels ? i.channels.map((ch) => ({ realId: ch.realId, id: ch.id })) : [],
                })),
                allOrgChannels: channels.edges.map(({ node }) => ({
                    realId: node.realId,
                    id: node.id,
                    groupKey: ScenarioApply.getChannelGroupKey(node.channelDef),
                })),
            });
        }

        const prefinishedAlreadyConnectedCount = alreadyConnectedChannels
            .filter((item) => {
                if (!item.channelDef.canBeAddedOnce) {
                    return false;
                }

                // Don't skip over a channel that just returned from OAuth — it still needs to complete its step.
                if (grantedChannelId && item.channel && item.channel.realId === grantedChannelId) {
                    return false;
                }

                const preset = presetSelection.channelPresetByGroup[item.groupKey];
                return !preset || preset.mode !== 'edit';
            })
            .length;

        // When returning from OAuth, jump directly to the granted channel's wizard step.
        // The prefix is 1 if there is an info step, because that step is always first in the list.
        let grantedChannelInitialStep = null;
        if (grantedChannelId) {
            const prefixSteps = showScenarioInfoStep ? 1 : 0;

            // Match by realId (raw UUID) or id (Relay global ID) to be safe.
            const channelMatches = (item) =>
                item.channel && (
                    item.channel.realId === grantedChannelId ||
                    item.channel.id === grantedChannelId
                );

            const grantedIdx = alreadyConnectedChannels.findIndex(channelMatches);

            if (grantedIdx >= 0) {
                grantedChannelInitialStep = prefixSteps + grantedIdx;
            } else {
                // Not in alreadyConnectedChannels — check channelsToSelect (org has multiple
                // channels of the same type, so they go through the chooser instead).
                // First try to match by channel realId/id, then fall back to channelDefId groupKey
                // for cases where the newly-created channel is not yet in the Relay cache.
                const selectIdx = channelsToSelect.findIndex((item) => {
                    if (item.channels && item.channels.some(
                        (ch) => ch.realId === grantedChannelId || ch.id === grantedChannelId
                    )) {
                        return true;
                    }
                    // Fallback: match by the channelDef realId or groupKey (the channel itself
                    // may not be in the Relay cache yet if it was just created by the OAuth flow).
                    if (grantedChannelDefId && item.channelDef) {
                        return item.channelDef.realId === grantedChannelDefId ||
                               item.channelDef.id === grantedChannelDefId ||
                               item.groupKey === grantedChannelDefId;
                    }
                    return false;
                });

                if (selectIdx >= 0) {
                    // Wizard order: prefix + alreadyConnected + channelsToConnect + index in choosers
                    grantedChannelInitialStep = prefixSteps + alreadyConnectedChannels.length + channelsToConnect.length + selectIdx;
                } else {
                    // Also check alreadyConnectedChannels by channelDef as fallback
                    const alreadyIdx = grantedChannelDefId
                        ? alreadyConnectedChannels.findIndex((item) =>
                            item.channelDef && (
                                item.channelDef.realId === grantedChannelDefId ||
                                item.channelDef.id === grantedChannelDefId ||
                                item.groupKey === grantedChannelDefId
                            )
                          )
                        : -1;

                    if (alreadyIdx >= 0) {
                        grantedChannelInitialStep = prefixSteps + alreadyIdx;
                    } else {
                        // Last resort: skip past prefix steps at minimum.
                        grantedChannelInitialStep = prefixSteps;
                    }
                }
            }

            if (typeof window !== 'undefined') {
                console.error('[ScenarioApply] grantedChannel resolution', {
                    grantedChannelId,
                    grantedChannelDefId,
                    prefixSteps,
                    grantedIdx,
                    grantedChannelInitialStep,
                    alreadyConnectedChannelIds: alreadyConnectedChannels.map((i) => i.channel ? i.channel.realId : null),
                    channelsToSelectWithIds: channelsToSelect.map((i) => ({
                        groupKey: i.groupKey,
                        channelDefRealId: i.channelDef ? i.channelDef.realId : null,
                        channelRealIds: i.channels ? i.channels.map((ch) => ch.realId) : [],
                    })),
                    allOrgChannelRealIds: channels.edges.map(({ node }) => node.realId),
                });
            }
        }

        const initialConfigurationStep = grantedChannelInitialStep !== null
            ? grantedChannelInitialStep
            : showScenarioInfoStep
                ? 0
                : (ScenarioPrerequisite.show(channelsToConnect) ? 0 : prefinishedAlreadyConnectedCount);

        this.state = {
            wasSuccess: false,
            flowSettingNeeded: false,
            flow: null,
            selectedChannels: [],
            channelSelect: presetSelection.channelSelect,
            channelPresetByGroup: presetSelection.channelPresetByGroup,
            lockedChannelGroups: presetSelection.lockedGroups,
            skippedChannelGroups: presetSelection.skippedGroups,
            preconfiguredFlowId,
            callbackOnSave,
            closeWindow,
            showScenarioInfoStep,
            grantedChannelId,
            grantedStep,
            grantedChannelDefId,
            isSaving: false,
            errors: null,
            configurationStep: initialConfigurationStep,
            activatingChannelId: null,
            alreadyConnectedChannels: alreadyConnectedChannels,
            channelsToConnect: channelsToConnect,
            resultLinks: [],
            scenario: scenario,
            channelsToSelect: channelsToSelect,
            channelsWithConfidentialMode: ScenarioApply.getChannelsWitConfidentialMode(scenario, channels),
            loading: true,
        };

        // let some time to detect (not)installed extenstion
        setTimeout(() => {
            this.configurationSteps = this.getConfigurationSteps();

            this.setState({
                loading: false
            });
        }, 50);
    }

    componentDidMount() {
        if (this.state.preconfiguredFlowId) {
            this.props.relay.setVariables({
                flowId: this.state.preconfiguredFlowId,
            });
        }

        // When returning from OAuth, the newly created channel may not be in the
        // Relay cache yet. Force a fresh fetch so the step calculation can find it.
        if (this.state.grantedChannelId) {
            const channels = this.props.user.organisation.channels;
            const isAlreadyInData = channels.edges.some(({ node }) =>
                node.realId === this.state.grantedChannelId || node.id === this.state.grantedChannelId
            );

            if (!isAlreadyInData) {
                this._grantedFetchDone = false;
                this.props.relay.forceFetch({}, () => {
                    if (this._grantedFetchDone) {
                        return;
                    }
                    this._grantedFetchDone = true;

                    const scenario = this.getScenario();
                    const freshChannels = this.props.user.organisation.channels;
                    const presetSelection = ScenarioApply.getPresetSelectionData(this.props, scenario, freshChannels);
                    let { alreadyConnectedChannels, channelsToConnect } = this.getChannelsToConnect(scenario, freshChannels);
                    alreadyConnectedChannels = ScenarioApply.filterBySkippedGroup(alreadyConnectedChannels, presetSelection.skippedGroups);
                    channelsToConnect = ScenarioApply.filterBySkippedGroup(channelsToConnect, presetSelection.skippedGroups);
                    const channelsToSelect = ScenarioApply.filterBySkippedGroup(ScenarioApply.getChannelsToSelect(scenario, freshChannels), presetSelection.skippedGroups);

                    const grantedChannelId = this.state.grantedChannelId;
                    const grantedChannelDefId = this.state.grantedChannelDefId;
                    const prefixSteps = this.state.showScenarioInfoStep ? 1 : 0;

                    const channelMatches = (item) =>
                        item.channel && (
                            item.channel.realId === grantedChannelId ||
                            item.channel.id === grantedChannelId
                        );

                    const grantedIdx = alreadyConnectedChannels.findIndex(channelMatches);
                    let newConfigStep = null;

                    if (grantedIdx >= 0) {
                        newConfigStep = prefixSteps + grantedIdx;
                    } else {
                        const selectIdx = channelsToSelect.findIndex((item) => {
                            if (item.channels && item.channels.some(
                                (ch) => ch.realId === grantedChannelId || ch.id === grantedChannelId
                            )) {
                                return true;
                            }
                            if (grantedChannelDefId && item.channelDef) {
                                return item.channelDef.realId === grantedChannelDefId ||
                                       item.channelDef.id === grantedChannelDefId ||
                                       item.groupKey === grantedChannelDefId;
                            }
                            return false;
                        });

                        if (selectIdx >= 0) {
                            newConfigStep = prefixSteps + alreadyConnectedChannels.length + channelsToConnect.length + selectIdx;
                        }
                    }

                    if (typeof window !== 'undefined') {
                        console.error('[ScenarioApply] grantedChannel forceFetch result', {
                            grantedChannelId,
                            grantedChannelDefId,
                            grantedIdx,
                            newConfigStep,
                            freshChannelRealIds: freshChannels.edges.map(({ node }) => node.realId),
                        });
                    }

                    if (newConfigStep !== null && newConfigStep !== this.state.configurationStep) {
                        this.setState({
                            configurationStep: newConfigStep,
                            alreadyConnectedChannels,
                            channelsToConnect,
                            channelsToSelect,
                        });
                    }
                });
            }
        }
    }

    onApplyScenarioClick(props) {
        this.setState({
            isSaving: true
        });

        if (!props.user.organisation) {
            return;
        }

        let scenario = props.user.scenario;

        if (!scenario) {
            scenario = props.user.scenarios.edges[0].node;
        }

        if (!scenarioRequiresPayPlan(scenario, props.user.organisation.payPlan)) {
            return;
        }

        EventTracker.trackEvent(EventTracker.EVENT_CLICKS, {
            event: 'scenario-apply',
            type: 'application'
        });

        return new Promise((resolve, reject) => {
            let onFailure = (transaction) => {
                mutationError(transaction);

                reject();
            };

            let onSuccess = (response) => {
                let {resultLinks, flows, errors} = response.applyScenario;
                let hasFlow = flows && flows.length > 0;

                if (errors) {
                    return this.setState({
                        isSaving: false,
                        errors: errors
                    });
                }

                sendToExtension({
                    message: 'dativery-reload'
                });

                if (hasFlow) {
                    this.props.relay.setVariables({
                        flowId: flows[0].realId
                    });
                }

                this.setState({
                    isSaving: false,
                    wasSuccess: true,
                    resultLinks: resultLinks || [],
                    flow: hasFlow ? flows[0] : null
                });

                resolve();

                props.relay.forceFetch();
            };

            Relay.Store.commitUpdate(new ApplyScenarioMutation({
                    scenario: scenario,
                    callbackOnSave: this.state.callbackOnSave,
                    organisation: props.user.organisation,
                    args: this.state.preconfiguredFlowId ? {flowId: this.state.preconfiguredFlowId} : null,
                    selectedChannels: Object.keys(this.state.channelSelect).map((key) => {
                        return {
                            channelDef_id: key,
                            channel_id: this.state.channelSelect[key].realId
                        };
                    })}),
                {
                    onFailure,
                    onSuccess
                }
            );
        });

    }

    onChannelSelect(channelDef, channel) {
        const groupKey = ScenarioApply.getChannelGroupKey(channelDef);
        if (this.state.lockedChannelGroups[groupKey] && this.state.channelSelect[groupKey] && this.state.channelSelect[groupKey].realId !== channel.realId) {
            return;
        }

        let channelSelect = this.state.channelSelect;
        channelSelect[groupKey] = channel;
        this.setState({channelSelect: channelSelect});
    }

    static filterBySkippedGroup(list, skippedGroups) {
        return list.filter((item) => !skippedGroups[item.groupKey]);
    }

    static normalizeChannelPresetKey(value) {
        return String(value || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-');
    }

    static parsePresetValue(rawValue) {
        if (!rawValue) {
            return null;
        }

        const tokens = String(rawValue)
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item.length > 0);

        if (tokens.length === 0) {
            return null;
        }

        let channelId = null;
        let mode = 'select';
        let locked = false;

        tokens.forEach((token, index) => {
            const lower = token.toLowerCase();
            if (lower === 'skip' || lower === 'edit' || lower === 'select') {
                mode = lower;
                return;
            }

            if (lower === 'locked' || lower === 'lock' || lower === 'readonly') {
                locked = true;
                return;
            }

            if (lower.startsWith('id:')) {
                channelId = token.substring(3).trim();
                return;
            }

            if (!channelId && index === 0) {
                channelId = token;
            }
        });

        if (!channelId) {
            return null;
        }

        return {
            channelId,
            mode,
            locked,
        };
    }

    static getRawQueryParams(props) {
        const raw = {};

        if (props && props.location && props.location.query) {
            Object.keys(props.location.query).forEach((key) => {
                raw[key] = props.location.query[key];
            });
        }

        const search = (props && props.location && props.location.search) || (typeof window !== 'undefined' && window.location ? window.location.search : '');
        if (!search) {
            return raw;
        }

        search
            .replace(/^\?/, '')
            .split('&')
            .filter((item) => item)
            .forEach((part) => {
                const delimiter = part.indexOf('=');
                const encodedKey = delimiter >= 0 ? part.substring(0, delimiter) : part;
                const encodedValue = delimiter >= 0 ? part.substring(delimiter + 1) : '';
                const key = decodeURIComponent(encodedKey || '').trim();
                const value = decodeURIComponent((encodedValue || '').replace(/\+/g, ' '));

                if (key) {
                    raw[key] = value;
                }
            });

        return raw;
    }

    static getChannelPresetsFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        const presets = {};

        Object.keys(rawParams).forEach((paramName) => {
            if (!paramName.startsWith('channel.') && !paramName.startsWith('channels.')) {
                return;
            }

            const key = paramName.replace(/^channels?\./, '').trim();
            const preset = ScenarioApply.parsePresetValue(rawParams[paramName]);
            if (!key || !preset) {
                return;
            }

            presets[ScenarioApply.normalizeChannelPresetKey(key)] = preset;
        });

        return presets;
    }

    static getFlowIdFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        return rawParams.flowId || rawParams.flow_id || rawParams.flow || null;
    }

    static getCallbackOnSaveFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        return rawParams.callbackOnSave || null;
    }

    static getCloseWindowFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        return rawParams.closeWindow || null;
    }

    static getShowScenarioInfoStepFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        const value = rawParams.showScenarioInfoStep || rawParams.showScenarioDescriptionStep || rawParams.showDescriptionStep;

        if (value === undefined || value === null) {
            return false;
        }

        const normalized = String(value).trim().toLowerCase();
        return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
    }

    static getGrantedChannelIdFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        return rawParams.grantedChannelId || null;
    }

    static getGrantedStepFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        const val = rawParams.grantedStep ? parseInt(rawParams.grantedStep, 10) : null;
        return val && !isNaN(val) ? val : null;
    }

    static getScenarioGroupsWithAliases(scenario) {
        const scenarioGroups = {};
        scenario.scenarios2ChannelDefs.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (scenarioGroups[groupKey]) {
                return;
            }

            const aliases = {};
            aliases[ScenarioApply.normalizeChannelPresetKey(groupKey)] = true;
            aliases[ScenarioApply.normalizeChannelPresetKey(node.channelDef.realId)] = true;
            aliases[ScenarioApply.normalizeChannelPresetKey(node.channelDef.id)] = true;
            aliases[ScenarioApply.normalizeChannelPresetKey(node.channelDef.name)] = true;
            aliases[ScenarioApply.normalizeChannelPresetKey(node.key)] = true;
            aliases[ScenarioApply.normalizeChannelPresetKey(node.ruleType)] = true;

            scenarioGroups[groupKey] = {
                groupKey,
                aliases,
            };
        });

        return scenarioGroups;
    }

    static getPresetSelectionData(props, scenario, channels) {
        const presets = ScenarioApply.getChannelPresetsFromQueryParams(props);
        const scenarioGroups = ScenarioApply.getScenarioGroupsWithAliases(scenario);
        const channelsByGroup = {};

        channels.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (!channelsByGroup[groupKey]) {
                channelsByGroup[groupKey] = [];
            }
            channelsByGroup[groupKey].push(node);
        });

        const channelSelect = {};
        const channelPresetByGroup = {};
        const lockedGroups = {};
        const skippedGroups = {};

        const matchesPresetChannelId = (channel, presetChannelId) => {
            if (!channel || !presetChannelId) {
                return false;
            }

            return channel.realId === presetChannelId || channel.id === presetChannelId;
        };

        const findGroupByChannelId = (presetChannelId) => {
            const candidateGroups = Object.keys(channelsByGroup).filter((groupKey) => {
                if (!scenarioGroups[groupKey]) {
                    return false;
                }

                return (channelsByGroup[groupKey] || []).some((channel) => matchesPresetChannelId(channel, presetChannelId));
            });

            if (candidateGroups.length === 1) {
                return candidateGroups[0];
            }

            return null;
        };

        Object.keys(presets).forEach((presetKey) => {
            const preset = presets[presetKey];
            let matchingGroup = Object.keys(scenarioGroups).find((groupKey) => scenarioGroups[groupKey].aliases[presetKey]);

            if (!matchingGroup) {
                matchingGroup = findGroupByChannelId(preset.channelId);
            }

            if (!matchingGroup) {
                if (typeof window !== 'undefined') {
                    console.error('[ScenarioApply] preset key did not match any scenario group', {presetKey, preset, scenarioGroups: Object.keys(scenarioGroups)});
                }
                return;
            }

            const candidateChannels = channelsByGroup[matchingGroup] || [];
            const selectedChannel = candidateChannels.find((channel) => matchesPresetChannelId(channel, preset.channelId));

            if (!selectedChannel) {
                if (typeof window !== 'undefined') {
                    console.error('[ScenarioApply] preset channel was not found in organisation channels', {
                        presetKey,
                        preset,
                        matchingGroup,
                        candidateChannelIds: candidateChannels.map((ch) => ch.realId || ch.id),
                    });
                }
                return;
            }

            channelSelect[matchingGroup] = selectedChannel;
            channelPresetByGroup[matchingGroup] = preset;

            if (preset.locked) {
                lockedGroups[matchingGroup] = true;
            }

            if (preset.mode === 'skip') {
                skippedGroups[matchingGroup] = true;
            }
        });

        return {
            channelSelect,
            channelPresetByGroup,
            lockedGroups,
            skippedGroups,
        };
    }

    nextStep() {
        this.setState({
            configurationStep: this.state.configurationStep + 1
        });
    }

    getTitle(scenario) {
        const metadata = types[this.typeName];

        let title = Tool.getDataForColumn('title', metadata.fields(), scenario);
        if (!title) {
            title = Tool.getDataForColumn('name', metadata.fields(), scenario);
        }

        return title;
    }

    getScenario() {
        let scenario = this.props.user.scenario;

        if (!scenario) {
            scenario = this.props.user.scenarios.edges[0].node;
        }

        return scenario;
    }

    renderResultLinks() {
        return (
            <div>
                {this.state.resultLinks.length ? <h4><FormattedMessage defaultMessage="Do you want to see the scenario in action?" id="channelWizard.links.title" /></h4> : null}
                <div className={"scenario-apply-links" + (this.props.user.organisation.flow ? ' withFlow' : '')}>
                {this.state.resultLinks.map((resultLink, idx) => {
                    if (!resultLink.url) {
                        return (
                            <div className="scenario-apply-one" key={idx}>
                                <div className="title">
                                    <FormattedMessage defaultMessage="Open {channelDefName} and go to {sourceDefName}" id="channelWizard.links.name" values={{channelDefName: resultLink.channelDef.name, sourceDefName: resultLink.sourceDef.name}} />
                                </div>
                            </div>
                        );
                    } else if (resultLink.linksToSourceDef) {
                        return (
                            <div className="scenario-apply-one" key={idx}>
                                <div className="title">
                                    <a className="btn btn-default" href={resultLink.url} target="_blank">
                                        <ChannelIcon channelDef={resultLink.channelDef} size={32}/>{" "}
                                        <FormattedMessage defaultMessage="Open {channelDefName} and go to {sourceDefName}" id="channelWizard.links.name" values={{channelDefName: resultLink.channelDef.name, sourceDefName: resultLink.sourceDef.name}} />
                                    </a>
                                </div>
                            </div>
                        );
                    } else {
                        return (
                            <div className="scenario-apply-one" key={idx}>
                                <div className="title">
                                    <FormattedMessage defaultMessage="Open {channelDefName} and go to {sourceDefName}" id="channelWizard.links.name" values={{channelDefName: resultLink.channelDef.name, sourceDefName: resultLink.sourceDef.name}} />
                                </div>
                                <div className="action">
                                    <a className="btn btn-default" href={resultLink.url} target="_blank">
                                        <ChannelIcon channelDef={resultLink.channelDef} size={32}/>{" "}
                                        <FormattedMessage defaultMessage="Go to {channelDefName}" id="channelWizard.links.actionGo" values={{channelDefName: resultLink.channelDef.name}} /></a>
                                </div>
                            </div>
                        );
                    }
                })}

                {this.props.user.organisation.flow ?
                    <div className="scenario-flow-results">
                        <h4><FormattedMessage defaultMessage="Scenario is activated." id="channelWizard.scenaioActivated" /></h4>
                        {this.props.user.organisation.flow.flowDef && this.props.user.organisation.flow.flowDef.appliedDescription ? <div className="secondStepper">
                            <h4><FormattedMessage defaultMessage="How to use this flow scenario:" id="channelWizard.flowUsage.title" /></h4>
                            <AppliedFlowDescription flow={this.props.user.organisation.flow} user={this.props.user} />
                        </div> : null}


                        {this.state.closeWindow ?
                            <p className="">
                                <Link className="btn btn-primary" style={{marginRight: '2em'}} onClick={() => window.close()}><FormattedMessage defaultMessage="Close window and go back" id="channelWizard.flowUsage.closeWindow" /></Link>
                                <Link className="btn btn-primary" to={urlMapping.getUrl(types['flows'], "config", this.props.user.organisation.flow)}><FormattedMessage defaultMessage="Change settings" id="channelWizard.flowUsage.toFlowConfig" /></Link>
                                
                            </p>
                        :
                            <p><Link className="btn btn-primary" to={urlMapping.getUrl(types['flows'], "view", this.props.user.organisation.flow)}><FormattedMessage defaultMessage="Go to flow detail" id="channelWizard.flowUsage.toDetail" /></Link></p>
                        }
                    </div>
                : null}
                </div>
            </div>
        );
    }

    renderChannelChoosers() {
        let channelGroups = (this.state.channelsToSelect || []).map((group) => {
            let {channelSelect, channelsWithConfidentialMode} = this.state;
            const groupKey = group.groupKey;
            const selectedChannel = channelSelect[groupKey];

            return {
                channelDef: group.channelDef,
                groupKey: groupKey,
                channels: group.channels.map((ch) => {
                    const isActive = Boolean(selectedChannel && selectedChannel.realId === ch.realId);
                    const hasError = channelsWithConfidentialMode ? channelsWithConfidentialMode.find(channelWithError => ch.id === channelWithError.id) : false;
                    return {
                        ...ch,
                        isActive,
                        hasError,
                    };
                }),
            };
        });

        // Sort by order field (scenario definition order)
        channelGroups.sort((groupA, groupB) => {
            let groupAOrder = this.state.scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === groupA.channelDef.realId).order;
            let groupBOrder = this.state.scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === groupB.channelDef.realId).order;

            return groupAOrder - groupBOrder;
        });

        return channelGroups.map((channelGroup, index) => {
            const preset = this.state.channelPresetByGroup[channelGroup.groupKey];
            const isPresetEdit = Boolean(preset && preset.mode === 'edit');
            const isLockedPreset = Boolean(this.state.lockedChannelGroups[channelGroup.groupKey] && this.state.channelSelect[channelGroup.groupKey]);

            // Check if the granted channel (returned from OAuth) is in this chooser group.
            const grantedChannelId = this.state.grantedChannelId;
            const grantedStep = this.state.grantedStep;
            const grantedChannelDefId = this.state.grantedChannelDefId;

            const isGrantedInGroup = Boolean(
                grantedChannelId &&
                channelGroup.channels.some((ch) =>
                    ch.realId === grantedChannelId || ch.id === grantedChannelId
                )
            );

            // Also match by channelDef when the specific channel isn't in Relay data yet
            // (e.g. freshly created via OAuth and not yet in the Relay cache).
            const isGrantedGroupByDef = Boolean(
                !isGrantedInGroup &&
                grantedChannelId &&
                grantedChannelDefId &&
                channelGroup.channelDef && (
                    channelGroup.channelDef.realId === grantedChannelDefId ||
                    channelGroup.channelDef.id === grantedChannelDefId ||
                    channelGroup.groupKey === grantedChannelDefId
                )
            );

            const isGrantedGroup = isGrantedInGroup || isGrantedGroupByDef;

            // When the granted channel isn't in Relay data, inject a synthetic placeholder
            // so ChannelChooser can navigate to ScenarioChannelConnect for it directly.
            // ScenarioChannelConnect will set channelId in its queryConfig and fetch it individually.
            const channelGroupToPass = (isGrantedGroupByDef && grantedChannelId)
                ? {
                    ...channelGroup,
                    channels: [
                        ...channelGroup.channels,
                        { realId: grantedChannelId, id: grantedChannelId, channelDef: channelGroup.channelDef, name: null },
                    ],
                }
                : channelGroup;

            return {
                title: (isPresetEdit && isLockedPreset)
                    ? (<FormattedMessage defaultMessage="Configure {app}" id="channelWizard.configureApplicationTitle" values={{app: channelGroup.channelDef.name}} />)
                    : (<FormattedMessage defaultMessage="Select {app} from multiple connections" id="channelWizard.selectApplicationTitle" values={{app: channelGroup.channelDef.name}} />),
                description: (
                    <ChannelChooser
                        key={isGrantedGroup ? ('granted-' + grantedStep) : undefined}
                        channelGroup={channelGroupToPass}
                        connectCallback={(channel) => {
                            this.connectChannelStep4Callback(channel);
                        }}
                        nextStep={() => { return this.nextStep(); }}
                        onChannelSelect={(channelDef, channel) => { return this.onChannelSelect(channelDef, channel); }}
                        locked={isGrantedGroup ? true : Boolean(this.state.lockedChannelGroups[channelGroup.groupKey])}
                        preselectedChannelId={isGrantedGroup ? grantedChannelId : (this.state.channelSelect[channelGroup.groupKey] ? this.state.channelSelect[channelGroup.groupKey].realId : null)}
                        forceConfigure={isPresetEdit || isGrantedGroup}
                        grantedChannelId={isGrantedGroup ? grantedChannelId : undefined}
                        grantedStep={isGrantedGroup ? grantedStep : undefined}
                        organisationId={this.props.user.organisation.realId}
                        scenario={this.state.scenario}
                    />
                )
            };
        });
    }

    /**
     * Group key for "extended" channelDefs.
     * If channelDef is derived (extendedFrom), treat it as the parent for selection/matching.
     */
    static getChannelGroupKey(channelDef) {
        // Prefer raw id field when present (more reliable than nested relation in older queries)
        if (channelDef && channelDef.extendedFrom_id) {
            return channelDef.extendedFrom_id;
        }
        if (channelDef && channelDef.extendedFrom && channelDef.extendedFrom.realId) {
            return channelDef.extendedFrom.realId;
        }
        return channelDef ? channelDef.realId : null;
    }

    renderConfidentialChannels() {
        let confidentialChannelsCount = this.state.channelsWithConfidentialMode.length;

        return (
            <div>
                <p>
                    <FormattedMessage defaultMessage="Scenario requires data caching but confidential mode preventing data caching is activated on {count, plural, one {this channnel} few {these channnels} other {these channnels}}:" id="scenarioApply.chaching.channelsHeader" values={{count: confidentialChannelsCount}} />
                </p>
                <div className="channelsWithConfidentialMode">
                    {
                        this.state.channelsWithConfidentialMode.map((channel, idx) => {
                            return (<div key={idx}>
                                <ChannelIcon channelDef={channel.channelDef} size={32}/>&nbsp;{channel.name ? channel.name : channel.channelDef.name}
                            </div>);
                        })
                    }
                </div>
                <div>
                    <Button className="btn btn-primary" onClick={() => {this.nextStep();}}>
                        <FormattedMessage defaultMessage="Disable confidential mode" id="channel.disableConfidential" />
                    </Button>
                </div>
            </div>
        );
    }

    renderFlowSettings(activeStep) {
        if (!activeStep) {
            return;
        }

        if (!this.finalSaving && !this.state.errors) {
            this.finalSaving = true;
            this.onApplyScenarioClick(this.props);
        }

        if (this.state.isSaving) {
            return (
                <LoadingLine withMessage>
                    <FormattedMessage defaultMessage="Activating scenario…" id="scanarioActivation.message" />
                </LoadingLine>
            );
        }

        if (this.state.errors) {
            return this.renderError();
        }

        if (this.state.flow) {
            try {
                let configOptions = JSON.parse(this.state.flow.configOptions);
                if (Object.keys(configOptions).length === 0) {
                    return this.nextStep();
                }
            } catch (e) {
                /* ignore error */
            }

            const queryConfig = {
                queries: meQuery,
                name: 'User',
                params: {
                    organisationId: this.props.user.organisation.realId,
                    scenarioId: this.state.scenario.realId,
                    flowId: this.state.flow.realId
                }
            };

            return (<Relay.Renderer
                Container={FlowConfig}
                environment={Relay.Store}
                queryConfig={queryConfig}
                render={(state) => {
                    if (state.done && this.step !== this.state.configurationStep) {
                        this.step = this.state.configurationStep;
                        return (<FlowConfig {...state.props} createCallback={(channel) => {this.nextStep();}} createStep wrapper />);
                    }
                    else if (!state.done) {
                        return (<LoadingLine withMessage>
                            <FormattedMessage defaultMessage="Loading flow settings…" id="scanarioActivation.loadingFlowSettings" />
                        </LoadingLine>);
                    }
                }}
            />);
        } else {
            return (<FormattedMessage defaultMessage="Scenario could not be applied because of a missing flow. If the issue persists, please try again later" id="scenarioApply.couldNotApplyMissingFlow"/>);
        }
    }

    getChannelsToConnect(scenario, channels) {
        // Scenario required groups (derived channelDefs are grouped by parent)
        let scenarioGroups = {};
        scenario.scenarios2ChannelDefs.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (!scenarioGroups[groupKey]) {
                scenarioGroups[groupKey] = node.channelDef;
            }
        });
        const scenarioGroupKeys = Object.keys(scenarioGroups);

        // Organisation channels grouped by groupKey (parent for derived)
        let channelGrouped = {};
        channels.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (!channelGrouped[groupKey]) {
                channelGrouped[groupKey] = [];
            }
            channelGrouped[groupKey].push(node);
        });

        let channelsToConnect = [];
        let alreadyConnectedChannels = [];

        scenarioGroupKeys.forEach((groupKey) => {
            if (!channelGrouped[groupKey]) {
                channelsToConnect.push({
                    groupKey,
                    channelDef: scenarioGroups[groupKey],
                });
            } else if (channelGrouped[groupKey].length === 1) {
                alreadyConnectedChannels.push({
                    groupKey,
                    channelDef: scenarioGroups[groupKey],
                    channel: channelGrouped[groupKey][0],
                });
            }
        });

        return {
            alreadyConnectedChannels: alreadyConnectedChannels.sort((a, b) => {
                if (a.channelDef.canBeAddedOnce && b.channelDef.canBeAddedOnce) {
                    let aOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === a.channelDef.realId).order;
                    let bOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === b.channelDef.realId).order;
                    return aOrder - bOrder;
                }

                if (a.channelDef.canBeAddedOnce) {
                    return -1;
                }
                if (b.channelDef.canBeAddedOnce) {
                    return 1;
                }

                let aOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === a.channelDef.realId).order;
                let bOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === b.channelDef.realId).order;
                return aOrder - bOrder;
            }),
            channelsToConnect: channelsToConnect.sort((a, b) => {
                let aOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === a.channelDef.realId).order;
                let bOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === b.channelDef.realId).order;
                return aOrder - bOrder;
            }),
        };
    }

    static getChannelsToSelect(scenario, channels) {
        // Scenario groups (derived channelDefs are grouped by parent)
        let scenarioGroups = {};
        scenario.scenarios2ChannelDefs.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (!scenarioGroups[groupKey]) {
                scenarioGroups[groupKey] = node.channelDef;
            }
        });

        // Organisation channels grouped by groupKey
        let channelGrouped = {};
        channels.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (!channelGrouped[groupKey]) {
                channelGrouped[groupKey] = [];
            }
            channelGrouped[groupKey].push(node);
        });

        let groups = [];
        Object.keys(channelGrouped).forEach((groupKey) => {
            if (scenarioGroups[groupKey] && channelGrouped[groupKey].length > 1) {
                groups.push({
                    groupKey,
                    channelDef: scenarioGroups[groupKey],
                    channels: channelGrouped[groupKey],
                });
            }
        });

        return groups.sort((a, b) => {
            let aOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === a.channelDef.realId).order;
            let bOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === b.channelDef.realId).order;
            return aOrder - bOrder;
        });
    }

    static getChannelsWitConfidentialMode(scenario, channels) {
        if (!scenario.requiresCaching) {
            return [];
        }

        let scenarioGroupKeys = {};
        scenario.scenarios2ChannelDefs.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            scenarioGroupKeys[groupKey] = true;
        });

        let channelGrouped = {};
        channels.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (!channelGrouped[groupKey]) {
                channelGrouped[groupKey] = [];
            }
            channelGrouped[groupKey].push(node);
        });

        let channelsWithConfidentialMode = [];
        Object.keys(channelGrouped).forEach((groupKey) => {
            if (scenarioGroupKeys[groupKey] && channelGrouped[groupKey].length === 1 && channelGrouped[groupKey][0].confidential) {
                channelsWithConfidentialMode.push(channelGrouped[groupKey][0]);
            }
        });

        return channelsWithConfidentialMode;
    }

    static showFlowSettings(scenario) {
        if (!scenario.scenarioDef.scenarioFlowDefs || !scenario.scenarioDef.scenarioFlowDefs.edges || 0 === scenario.scenarioDef.scenarioFlowDefs.edges.length) {
            return false;
        }

        let flowsWithSettings = scenario.scenarioDef.scenarioFlowDefs.edges.filter(({node}) => !Boolean(node.hideFlowSettingsDuringScenarioApply));

        return flowsWithSettings.length > 0;
    }

    connectChannelStep4Callback(channel) {
        let nextStep = this.state.configurationStep + 1;

        this.onChannelSelect(channel.channelDef, channel);

        // NOTE: forceFetch callback could be called multiple times on one invocation
        this.props.relay.forceFetch({}, () => {
            let scenario = this.getScenario();
            let channels = this.props.user.organisation.channels;

            let {alreadyConnectedChannels, channelsToConnect} = this.getChannelsToConnect(scenario, channels);
                alreadyConnectedChannels = ScenarioApply.filterBySkippedGroup(alreadyConnectedChannels, this.state.skippedChannelGroups);
                channelsToConnect = ScenarioApply.filterBySkippedGroup(channelsToConnect, this.state.skippedChannelGroups);
                const channelsToSelect = ScenarioApply.filterBySkippedGroup(ScenarioApply.getChannelsToSelect(scenario, channels), this.state.skippedChannelGroups);

            this.setState({
                configurationStep: nextStep,
                alreadyConnectedChannels: alreadyConnectedChannels,
                channelsToConnect: channelsToConnect,
                    channelsToSelect: channelsToSelect,
            });
        });
    }

    renderChannelsToConnect() {
        let channelsToConnect = this.state.channelsToConnect;

        if (channelsToConnect.length === 0 && this.state.alreadyConnectedChannels.length <= 0) {
            return [];
        }

        let channelsToConnectRender = [];

        channelsToConnect.forEach((item, index) => {
            channelsToConnectRender.push({
                title: (<FormattedMessage defaultMessage="Connect {applicationName}" id="channelWizard.connectApplicationName" values={{applicationName: item.channelDef.name}} />),
                description: (<ScenarioChannelConnect
                    channelDef={item.channelDef}
                    connectCallback={(channel) => {
                        return this.connectChannelStep4Callback(channel);
                    }}
                    organisationId={this.props.user.organisation.realId}
                    scenario={this.state.scenario}
                />)
            });
        });

        return channelsToConnectRender;
    }

    renderAlreadyConnectedChannels() {
        return this.state.alreadyConnectedChannels
            .filter((item) => !this.state.skippedChannelGroups[item.groupKey])
            .map((item) => {
            const preset = this.state.channelPresetByGroup[item.groupKey];
            const needsManualEditStep = Boolean(preset && preset.mode === 'edit');
            const isGrantedChannel = Boolean(
                this.state.grantedChannelId &&
                item.channel &&
                (
                    item.channel.realId === this.state.grantedChannelId ||
                    item.channel.id === this.state.grantedChannelId
                )
            );

            if (typeof window !== 'undefined' && this.state.grantedChannelId) {
                console.error('[ScenarioApply] renderAlreadyConnectedChannels channel', {
                    groupKey: item.groupKey,
                    channelRealId: item.channel ? item.channel.realId : null,
                    channelId: item.channel ? item.channel.id : null,
                    grantedChannelId: this.state.grantedChannelId,
                    isGrantedChannel,
                    needsManualEditStep,
                    grantedStep: this.state.grantedStep,
                });
            }

            return {
                finished: !needsManualEditStep && !isGrantedChannel,
                title: (needsManualEditStep || isGrantedChannel)
                    ? (<FormattedMessage defaultMessage="Configure {app}" id="channelWizard.configureApplicationTitle" values={{app: item.channelDef.name}} />)
                    : (<FormattedMessage defaultMessage="Connected {applicationName}" id="channelWizard.connectedApplicationName" values={{applicationName: item.channelDef.name}} />),
                description: (
                    <ChannelChooser
                        key={isGrantedChannel ? ('granted-' + this.state.grantedStep) : undefined}
                        channelGroup={{
                            channels: [item.channel],
                            channelDef: item.channelDef,
                            groupKey: item.groupKey,
                        }}
                        connectCallback={(channel) => {
                            return this.connectChannelStep4Callback(channel);
                        }}
                        nextStep={() => { return this.nextStep(); }}
                        onChannelSelect={(channelDef, channel) => { return this.onChannelSelect(channelDef, channel); }}
                        locked={Boolean(this.state.lockedChannelGroups[item.groupKey])}
                        preselectedChannelId={this.state.channelSelect[item.groupKey] ? this.state.channelSelect[item.groupKey].realId : null}
                        forceConfigure={needsManualEditStep || isGrantedChannel}
                        initialStep={isGrantedChannel ? this.state.grantedStep : undefined}
                        grantedChannelId={this.state.grantedChannelId}
                        grantedStep={this.state.grantedStep}
                        organisationId={this.props.user.organisation.realId}
                        scenario={this.state.scenario}
                    />
                )
            };
        });
    }

    renderScenarioInfoStep() {
        const metadata = types[this.typeName];
        const scenario = this.state.scenario;
        const privacy = Tool.getFormatted(metadata, 'privacy', this.props.intl.formatMessage, scenario);
        const longDescription = Tool.getFormatted(metadata, 'longDescription', this.props.intl.formatMessage, scenario);

        return {
            title: (<FormattedMessage defaultMessage="Scenario information" id="scenarioApply.infoStep.title" />),
            description: (
                <div>
                    {privacy ? (
                        <div className="scenario-note big">
                            <span><strong>{Tool.getLabel(types['scenarios'], 'privacy', this.props.intl.formatMessage)}:</strong> {privacy}<br/></span>
                        </div>
                    ) : null}
                    {longDescription ? <div className="longDescription">{longDescription}</div> : null}
                    <a className="btn btn-primary" href="#" onClick={() => { this.nextStep(); return false; }}>
                        <FormattedMessage defaultMessage="Next step" id="channelWizard.skipStep" />
                    </a>
                </div>
            )
        };
    }

    renderError() {
        return (
            <Alert bsStyle="danger">
                <FormattedMessage defaultMessage="Error during scenario apply: {message}" id="channelWizard.apply.error" values={{message: (<strong>{this.state.errors}</strong>)}} />
            </Alert>
        );
    }

    renderFinishStep(finalStep) {
        if (!finalStep) {
            return;
        }

        if (!this.finalSaving && !this.state.errors) {
            this.finalSaving = true;
            this.onApplyScenarioClick(this.props);
        }

        if (this.state.isSaving) {
            return (
                <LoadingLine withMessage>
                    <FormattedMessage defaultMessage="Activating scenario…" id="scanarioActivation.message" />
                </LoadingLine>
            );
        }

        if (this.state.errors) {
            return this.renderError();
        }

        return this.renderResultLinks();
    }

    getDestinationChannelDef() {
        let destinationDefs = this.state.scenario.scenarios2ChannelDefs.edges.filter(({node}) => node.ruleType === 'destination');

        if (destinationDefs.length === 0) {
            return false;
        }

        try {
            return destinationDefs[0].node.channelDef;
        }
        catch (e) {
            return false;
        }
    }

    needInstallExtensionStep() {
        let destinationChannelDef = this.getDestinationChannelDef();

        if (!Boolean(destinationChannelDef)) {
            return false;
        }

        if (this.state.scenario.requiresExtension === false) {
            return false;
        }

        let hasOwnPlugin = Boolean(destinationChannelDef.hasOwnPlugin);

        return !hasOwnPlugin && !isExtensionPresent();
    }

    renderInstallExtenstionStep() {
        let destinationChannelDef = this.getDestinationChannelDef();

        return (
            <div>
                <ChannelIcon channelDef={destinationChannelDef} size={32}/>{" "}<FormattedMessage defaultMessage="To use {channelDefName} with {productName} you must" id="channelWizard.extensionRequired" values={{channelDefName: destinationChannelDef.name, productName: config.PRODUCT_NAME}} />
                {" "}<a className="btn btn-primary" href={getExtensionURL()} onClick={() => {this.nextStep();}} target="_blank">
                    <FormattedMessage defaultMessage="install our Browser Extension" id="channelWizard.extensionRequiredButton" />
                </a>.
                <br /><br />
                <a className="btn btn-primary" href="#" onClick={() => { this.nextStep(); return false;}}>
                    <FormattedMessage defaultMessage="Next step" id="channelWizard.skipStep" />
                </a>
            </div>
        );
    }

    // renderConnectRequirements() {
    // 	let destinationChannelDef = this.getDestinationChannelDef();

    // 	if (!Boolean(destinationChannelDef) || !Boolean(destinationChannelDef.connectRequirement)) {
    // 		return null;
    // 	}

    // 	return (<div>
    // 		<ChannelIcon channelDef={destinationChannelDef} size={32}/>{" "}{Tool.getFormatted(types['channelDefs'], 'connectRequirement', this.props.intl.formatMessage, destinationChannelDef)}
    // 		<br /><br />
    // 		<a className="btn btn-primary" href="#" onClick={() => { this.nextStep(); return false;}}>
    // 			<FormattedMessage defaultMessage="Next step" id="channelWizard.skipStep" />
    // 		</a>
    // 	</div>);
    // }

    getConfigurationSteps() {
        let configurationList = [];
        let showFlowSettings = ScenarioApply.showFlowSettings(this.state.scenario);

        if (this.state.showScenarioInfoStep) {
            let introStep = this.renderScenarioInfoStep();
            if (introStep) {
                configurationList.push(introStep);
            }
        }

        if (this.state.alreadyConnectedChannels.length > 0) {
            configurationList.push(...this.renderAlreadyConnectedChannels());
        }

        configurationList.push(...this.renderChannelsToConnect());

        if (this.state.channelsToSelect.length > 0) {
            configurationList.push(...this.renderChannelChoosers());
        }

        if (this.state.channelsWithConfidentialMode.length > 0) {
            configurationList.push({
                title: (<FormattedMessage defaultMessage="Confidential mode notice" id="channelWizard.confidentialModeNotice" />),
                description: this.renderConfidentialChannels()
            });
        }

        if (showFlowSettings) {
            configurationList.push({
                title: (<FormattedMessage defaultMessage="Flow settings" id="channelWizard.flowSettingsTitle" />),
                description: this.renderFlowSettings(configurationList.length === this.state.configurationStep)
            });
        }

        if (this.needInstallExtensionStep()) {
            configurationList.push({
                title: (<FormattedMessage defaultMessage="Browser extension" id="channelWizard.installBrowserExtension" />),
                description: this.renderInstallExtenstionStep()
            });
        }

        //
        // let connectRequirements = this.renderConnectRequirements();
        // if (connectRequirements) {
        // 	configurationList.push({
        // 		title: (<FormattedMessage defaultMessage="Connect requirements" id="channelWizard.connectRequirements" />),
        // 		description: connectRequirements
        // 	});
        // }

        configurationList.push({
            title: (<FormattedMessage defaultMessage="Activation finished" id="channelWizard.activationFinished" />),
            description: this.renderFinishStep(configurationList.length === this.state.configurationStep)
        });

        return configurationList;
    }

    render() {
        let {scenario} = this.state;
        let type = types[this.typeName];
        let title = this.getTitle(scenario);

        if (this.state.loading) {
            return null;
        }

        this.configurationSteps = this.getConfigurationSteps();

        return (
            <AppPanel breadcrumbParams={[
                {type, data: scenario},
                {type: types['scenarios'], data: scenario},
                {type: types['organisations'], data: this.props.user.organisation}
            ]} className="scenarioWizzardNew" title={title}>

                <DetailBox className="scenarioWizard" data={scenario.scenarioDef} metadata={type} urlMapping={urlMapping}>
                    {ScenarioApply.getRawQueryParams().style === 'flexibee' && (
                        <div className="flexibeeHeader">
                            <img className="flexibeeHeaderAbra" src="/cdn/logos/logo-abra-flexi.svg" alt="ABRA Flexi logo"  />
                            <span className="flexibeeHeaderPlus">+</span>
                            <img className="flexibeeHeaderDativery" src="/cdn/dativery.svg" alt="Dativery logo" />
                        </div>
                    )}

                    <div className="scenario-header">
                        <div className="left">
                            <ScenarioIcon scenario={scenario} />
                        </div>
                        <div className="main" style={{padding: '0'}}>
                            <span className="flex"></span>
                            <h2>{this.state.showScenarioInfoStep && scenario.title ? Tool.getFormatted(type, 'title', this.props.intl.formatMessage, scenario) : <FormattedMessage defaultMessage="Scenario activation" id="channelWizard.activationHeader" />}</h2>
                            <span className="flex"></span>
                        </div>
                    </div>
                </DetailBox>

                <StepperList activeStep={this.state.configurationStep} list={this.configurationSteps} />
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(ScenarioApply), (props) => {
    return (props.user.scenarios && props.user.scenarios.edges.length) > 0 || props.user.scenario;
}), {
    initialVariables: {
        scenarioUrlFragment: '',
        scenarioUrlFragmentSelected: false,
        scenarioId: '',
        scenarioIdSelected: false,
        organisationId: '',
        organisationSelected: false,
        flowId: '',
        flowSelected: false,
    },
    prepareVariables (variables) {
        if (!variables.organisationId) {
            variables.organisationId = getLastOrganisationId();
        }

        variables['organisationSelected'] = variables['organisationId'] ? true : false;
        variables['flowSelected'] = variables['flowId'] ? true : false;
        variables['scenarioUrlFragmentSelected'] = variables['scenarioUrlFragment'] ? true : false;
        variables['scenarioIdSelected'] = variables['scenarioId'] ? true : false;

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                firstname,
                surname,
                email,
                ${AppliedFlowDescription.getFragment('user')}
                organisation(id: $organisationId) @include(if: $organisationSelected) {
                    id,
                    realId,
                    name,
                    payPlan,
                    ${ScenarioBoxAsWizard.getFragment('organisation')},
                    channels(first: 1000, deleted: false) {
                        edges {
                            node {
                                name,
                                type,
                                confidential,
                                cachingStatus,
                                activated,
                                metadata,
                                lockedAdding,
                                channelDef {
                                    id,
                                    realId,
                                    extendedFrom_id,
                                    extendedFrom {
                                        id,
                                        realId,
                                        icon
                                    },
                                    icon,
                                    addType,
                                    name,
                                    state,
                                    hasOwnPlugin,
                                    metadata,
                                    canBeAddedOnce
                                },
                                id,
                                realId,
                                organisation_id,
                            }
                        },
                    },
                    flow(id: $flowId) @include(if: $flowSelected) {
                        id,
                        realId,
                        organisation_id,
                        configOptions,
                        flowDef {
                            id,
                            realId,
                            appliedDescription,
                        }
                        ${AppliedFlowDescription.getFragment('flow')}
                    },
                    usedScenariosByURL(urlFragment: $scenarioUrlFragment, first: 10) @include(if: $scenarioUrlFragmentSelected) {
                        edges {
                            node {
                                id,
                                realId,
                                scenario_id,
                                scenarioDef_id,
                            }
                        }
                    },
                },
                organisations(first: 10) {
                    edges {
                        node {
                            id,
                        }
                    }
                },
                scenarios(first: 1, urlFragment: $scenarioUrlFragment) @include(if: $scenarioUrlFragmentSelected) {
                    edges {
                        node {
                            id,
                            realId,
                            name,
                            title,
                            description,
                            privacy,
                            longDescription,
                            urlFragment,
                            requiresCaching,
                            requiresExtension,
                            scenarioDef {
                                scenarioFlowDefs(first: 100) {
                                    edges {
                                        node {
                                            realId
                                        }
                                    }

                                }
                            }
                            scenarios2ChannelDefs(first: 100) {
                                edges {
                                    node {
                                        id,
                                        realId,
                                        key,
                                        ruleType,
                                        order,
                                        channelDef {
                                            id,
                                            realId,
                                            extendedFrom_id,
                                            extendedFrom {
                                                id,
                                                realId,
                                                icon
                                            },
                                            name,
                                            icon,
                                            metadata,
                                            hasOwnPlugin,
                                            connectRequirement,
                                            requiresAgent,
                                            canBeAddedOnce
                                        }
                                    }
                                }
                            }
                            ${ScenarioBoxAsWizard.getFragment('scenario')},
                            ${ScenarioIcon.getFragment('scenario')}
                        }
                    }
                },
                scenario(id: $scenarioId) @include(if: $scenarioIdSelected) {
                    id,
                    realId,
                    name,
                    title,
                    description,
                    privacy,
                    longDescription,
                    urlFragment,
                    requiresCaching,
                    requiresExtension,
                    scenarioDef {
                        scenarioFlowDefs(first: 100) {
                            edges {
                                node {
                                    realId,
                                    hideFlowSettingsDuringScenarioApply
                                }
                            }

                        }
                    },
                    scenarios2ChannelDefs(first: 100) {
                        edges {
                            node {
                                id,
                                realId,
                                key,
                                ruleType,
                                order,
                                channelDef {
                                    id,
                                    realId,
                                    extendedFrom_id,
                                    extendedFrom {
                                        id,
                                        realId,
                                        icon
                                    },
                                    name,
                                    icon,
                                    metadata,
                                    hasOwnPlugin,
                                    connectRequirement,
                                    requiresAgent,
                                    canBeAddedOnce
                                }
                            }
                        }
                    }
                    ${ScenarioBoxAsWizard.getFragment('scenario')},
                    ${ScenarioIcon.getFragment('scenario')}
                },
            }
        `,
    },
});
